import { ReferenceDetails } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import { Button } from 'braid-design-system';
import { useEffect, useMemo } from 'react';

import { Page, PageSection, useTrackingClient } from 'src/context/tracking';
import { useGetCurrentPage } from 'src/hooks/useGetCurrentPage';
import { useEditorDrawer } from 'src/ui/EditorDrawer/EditorDrawer';

import translations from './.vocab';
import type { ModifiedReferenceCheck } from './ReferencesList';

export function ReferenceDetailsViewer({
  referenceChecks,
}: {
  referenceChecks: ModifiedReferenceCheck[];
}) {
  const client = useTrackingClient();
  const { t } = useTranslations(translations);
  const { editingId, switchToReferenceChecks } = useEditorDrawer();
  const ids = editingId ? editingId.split('~') : [];
  const referenceCheck = referenceChecks.find((r) => r.id === ids[0]);
  const reference = referenceCheck?.references[Number(ids[1])];
  const currentPage = useGetCurrentPage();
  const trackingPageProps = useMemo(
    () => ({
      currentPage,
      currentPageSection: PageSection.REFERENCES,
      ...(currentPage === Page.REVIEW_PAGE && {
        currentPageSubsection: 'references',
      }),
    }),
    [currentPage],
  );

  useEffect(() => {
    if (editingId) {
      client.trackView({
        view: currentPage,
        eventName: 'profile_section_displayed',
        ...trackingPageProps,
        ...(currentPage === Page.REVIEW_PAGE && {
          currentPageSectionState: 'edit',
        }),
      });
    }
  }, [client, editingId, currentPage, trackingPageProps]);

  const trackOnReferenceCheckInfo = () => {
    client.trackLink({
      link: currentPage,
      eventName: 'completed_reference_check_pressed',
      ...trackingPageProps,
    });

    client.trackView({
      view: currentPage,
      eventName: 'certsy_dialog_displayed',
      ...trackingPageProps,
      certsyType: PageSection.REFERENCES,
    });
  };

  const trackOnManage = () => {
    client.trackLink({
      link: currentPage,
      eventName: 'certsy_manage_pressed',
      ...trackingPageProps,
    });

    client.trackView({
      view: currentPage,
      eventName: 'certsy_dialog_displayed',
      ...trackingPageProps,
      certsyType: PageSection.REFERENCES,
    });
  };

  const trackOnSeekPass = () =>
    client.trackLink({
      link: currentPage,
      eventName: 'certsy_dialog_pressed',
      ...trackingPageProps,
      certsyType: PageSection.REFERENCES,
      callToAction: 'continue to seek pass',
    });

  const trackOnLearnMore = () =>
    client.trackLink({
      link: currentPage,
      eventName: 'certsy_dialog_pressed',
      ...trackingPageProps,
      certsyType: PageSection.REFERENCES,
      callToAction: 'learn more',
    });

  const doneButton =
    currentPage === Page.REVIEW_PAGE ? (
      <Button
        variant="solid"
        tone="formAccent"
        onClick={switchToReferenceChecks}
      >
        {t('Done')}
      </Button>
    ) : undefined;

  if (editingId && referenceCheck && reference) {
    return (
      <ReferenceDetails
        {...{
          ...reference,
          toDate: reference?.toDate ?? null,
        }}
        primaryButton={doneButton}
        onManageClick={trackOnManage}
        onCompleteClick={trackOnReferenceCheckInfo}
        onContinueClick={trackOnSeekPass}
        onLearnMoreClick={trackOnLearnMore}
      />
    );
  }
  return null;
}
